import React, { useState, useEffect, useRef, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import CasesContext from '../../context/CasesContext';
import AacnContext from '../../context/AacnContext';
import {
  PATIENT_FIELDS,
  normalizeDraft,
  routeApproveErrorMessage,
  updateCase,
  publishCase,
  unpublishCase,
  addCollaborator,
  removeCollaborator,
} from '../../utils/caseDraft';
import { getCurrentUserEmail } from '../../utils/currentUser';
import { fetchAccounts } from '../../utils/accountsApi';
import { setLastCasesTab } from '../../utils/casesViewState';
import './CaseUpload.css';
import './CaseManage.css';
import Footer from '../Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://ns-dashboard-api.kabilah.com';

// Fixed checklist-generation config — no longer admin-configurable; all three
// sections always run at these defaults.
const DEFAULT_CONFIG = {
  missingInfo: { enabled: true, depth: 3, maxItems: 5 },
  accurateInfo: { enabled: true, depth: 2, maxItems: 5 },
  predictionInfo: { enabled: true, depth: 3, maxItems: 5 },
};

const SECTIONS = [
  { key: 'missingInfo', title: 'Missing Information', hint: 'Flags information that should have been included but wasn’t' },
  { key: 'accurateInfo', title: 'Accurate Information', hint: 'Checks what was said against the case’s actual details' },
  { key: 'predictionInfo', title: 'Prediction Information', hint: 'Highlights safety concerns specific to this patient' },
];

function ChecklistItemRow({ item, onEdit, onDelete, onDragStart, onDragOver, onDrop, finalized }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.text);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed) onEdit(item.id, trimmed);
    else setDraft(item.text);
    setEditing(false);
  };

  return (
    <div
      className={`checklist-item-row ${finalized ? 'checklist-item-finalized' : ''}`}
      draggable={!finalized}
      onDragStart={() => onDragStart(item.id)}
      onDragOver={(e) => { e.preventDefault(); onDragOver(item.id); }}
      onDrop={() => onDrop(item.id)}
    >
      {!finalized && <span className="drag-handle">⠿</span>}
      <div className="checklist-item-bullet" />
      {editing ? (
        <input
          ref={inputRef}
          className="checklist-item-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setDraft(item.text); setEditing(false); } }}
        />
      ) : (
        <>
          <span
            className={`checklist-item-text ${finalized ? '' : 'checklist-item-editable'}`}
            onClick={() => { if (!finalized) setEditing(true); }}
          >
            {item.text}
          </span>
        </>
      )}
      {!finalized && (
        <button className="checklist-item-delete" onClick={() => onDelete(item.id)}>×</button>
      )}
    </div>
  );
}

function ChecklistSection({ sectionKey, title, hint, items, onEdit, onDelete, onAdd, onDragStart, onDragOver, onDrop, finalized }) {
  const [newText, setNewText] = useState('');
  const [adding, setAdding] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (adding && inputRef.current) inputRef.current.focus();
  }, [adding]);

  const commitAdd = () => {
    const trimmed = newText.trim();
    if (trimmed) onAdd(sectionKey, trimmed);
    setNewText('');
    setAdding(false);
  };

  return (
    <div className="checklist-section">
      <div className="checklist-section-header">
        <span className="checklist-section-title">{title}</span>
        <span className="checklist-section-count">{items.length}</span>
        {hint && <span className="checklist-section-hint">{hint}</span>}
      </div>
      <div className="checklist-section-items">
        {items.map((item) => (
          <ChecklistItemRow
            key={item.id}
            item={item}
            onEdit={onEdit}
            onDelete={onDelete}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDrop={onDrop}
            finalized={finalized}
          />
        ))}
        {!finalized && (
          adding ? (
            <div className="checklist-add-row">
              <div className="checklist-item-bullet" />
              <input
                ref={inputRef}
                className="checklist-item-input"
                value={newText}
                placeholder="New item..."
                onChange={(e) => setNewText(e.target.value)}
                onBlur={commitAdd}
                onKeyDown={(e) => { if (e.key === 'Enter') commitAdd(); if (e.key === 'Escape') { setNewText(''); setAdding(false); } }}
              />
            </div>
          ) : (
            <button className="checklist-add-btn" onClick={() => setAdding(true)}>+ Add item</button>
          )
        )}
      </div>
    </div>
  );
}

let itemCounter = 0;
const makeId = () => `item-${++itemCounter}-${Date.now()}`;

function CaseManage({ isTeacher }) {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { cases, fetchCases } = useContext(CasesContext);
  const {
    subCompetencies: AACN_SUB_COMPETENCIES,
    getSubCompetencyByCode,
    loading: aacnLoading,
  } = useContext(AacnContext);
  const [caseName, setCaseName] = useState(cases.find((c) => c.id === caseId)?.name || '');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);
  const [collaboratorInput, setCollaboratorInput] = useState('');
  const [showCollaboratorSuggestions, setShowCollaboratorSuggestions] = useState(false);
  const [collaboratorError, setCollaboratorError] = useState('');
  const [addingCollaborator, setAddingCollaborator] = useState(false);
  const [removingCollaborator, setRemovingCollaborator] = useState(null);
  const [collaboratorAccounts, setCollaboratorAccounts] = useState([]);

  const currentUserEmail = getCurrentUserEmail();

  useEffect(() => {
    fetchAccounts(['admin', 'teacher'])
      .then((res) => { if (Array.isArray(res.data)) setCollaboratorAccounts(res.data); })
      .catch(() => {}); // Autocomplete is a nice-to-have — falls back to plain email entry.
  }, []);

  // Case data (editable)
  const [caseDraft, setCaseDraft] = useState(null);
  const [caseDataFieldErrors, setCaseDataFieldErrors] = useState({});
  const [caseDataErrorMsg, setCaseDataErrorMsg] = useState('');
  const [caseDataSavedMsg, setCaseDataSavedMsg] = useState('');
  const [savingCaseData, setSavingCaseData] = useState(false);
  const [indicatorSearch, setIndicatorSearch] = useState('');
  const [activeSection, setActiveSection] = useState(null);

  const [checklist, setChecklist] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(false);
  const [finalized, setFinalized] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [checklistLoading, setChecklistLoading] = useState(true);

  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/cases/${caseId}`)
      .then((res) => {
        if (res.data) {
          if (res.data.caseName) setCaseName(res.data.caseName);
          setCaseDraft(normalizeDraft(res.data));
        }
      })
      .catch(() => {});

    axios.get(`${BACKEND_URL}/api/cases/${caseId}/checklist`)
      .then((res) => {
        if (res.data) {
          if (res.data.items && res.data.items.length > 0) {
            setChecklist(res.data.items);
            setFinalized(res.data.finalized || false);
          }
        }
      })
      .catch(() => {}) // 404 = no checklist yet, show unconfigured state
      .finally(() => setChecklistLoading(false));
  }, [caseId]);

  const clearCaseDataFieldError = (field) => setCaseDataFieldErrors((prev) => {
    if (!prev[field]) return prev;
    const next = { ...prev };
    delete next[field];
    return next;
  });

  const setDraftField = (field, value) => {
    setCaseDraft((prev) => ({ ...prev, [field]: value }));
    clearCaseDataFieldError(field);
    setCaseDataSavedMsg('');
  };
  const setPatientField = (field, value) => {
    setCaseDraft((prev) => ({ ...prev, patient: { ...prev.patient, [field]: value } }));
    clearCaseDataFieldError(field);
    setCaseDataSavedMsg('');
  };
  const handleAgeChange = (value) => {
    setPatientField('age', value);
    const ageNum = parseInt(value, 10);
    if (isNaN(ageNum) || ageNum < 0) return;
    // DOB can't be recovered exactly from just an age, so pick a fixed
    // convention (Jan 1 of the birth year) — this also keeps it consistent
    // with the age-from-DOB calculation used at case-creation time, since a
    // Jan 1 birthday always rounds to the same age for the rest of the year.
    const birthYear = new Date().getFullYear() - ageNum;
    setPatientField('dateOfBirth', `${birthYear}-01-01`);
  };

  const setReportItemContents = (index, value) => {
    setCaseDraft((prev) => ({
      ...prev,
      reportItems: prev.reportItems.map((item, i) => i === index ? { ...item, contents: value } : item),
    }));
    setCaseDataSavedMsg('');
  };

  const toggleIndicatorCode = (code) => {
    if (isKabilahCase) return;
    const current = caseDraft.indicatorCodes || [];
    const next = current.includes(code) ? current.filter((c) => c !== code) : [...current, code];
    setDraftField('indicatorCodes', next);
  };

  const handleSaveCaseData = async () => {
    // Room and MRN are locked after creation (not editable on this page), so the
    // room-required check from validateApproveDraft doesn't apply here — re-running it
    // would create a dead end for older cases that predate that requirement, since there'd
    // be no way to fix a blank Room without the field being editable.
    setCaseDataFieldErrors({});
    setCaseDataErrorMsg('');
    setCaseDataSavedMsg('');
    setSavingCaseData(true);
    try {
      await updateCase(caseId, caseDraft);
      setCaseName(caseDraft.caseName);
      setCaseDataSavedMsg('Saved.');
      fetchCases();
    } catch (err) {
      const { fieldErrors: routedFieldErrors, generalError } = routeApproveErrorMessage(err.response?.data?.message);
      if (routedFieldErrors) setCaseDataFieldErrors(routedFieldErrors);
      if (generalError) setCaseDataErrorMsg(generalError);
    } finally {
      setSavingCaseData(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/cases/${caseId}`);
      fetchCases();
      navigate('/admin/cases');
    } catch {
      alert('Could not delete the case. Please try again.');
      setDeleting(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      await publishCase(caseId);
      setCaseDraft((prev) => ({ ...prev, status: 'published' }));
      fetchCases();
    } catch {
      alert('Could not publish this case. Please try again.');
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    setUnpublishing(true);
    try {
      await unpublishCase(caseId);
      // The backend adds every admin/teacher as a collaborator on unpublish —
      // re-fetch rather than guess that list client-side.
      const res = await axios.get(`${BACKEND_URL}/api/cases/${caseId}`);
      if (res.data) setCaseDraft(normalizeDraft(res.data));
      fetchCases();
    } catch {
      alert('Could not unpublish this case. Please try again.');
    } finally {
      setUnpublishing(false);
    }
  };

  const handleAddCollaborator = async (emailOverride) => {
    const email = (emailOverride || collaboratorInput).trim();
    if (!email) return;
    setAddingCollaborator(true);
    setCollaboratorError('');
    try {
      await addCollaborator(caseId, email);
      setCaseDraft((prev) => ({ ...prev, collaborators: [...(prev.collaborators || []), email] }));
      setCollaboratorInput('');
    } catch (err) {
      setCollaboratorError(err.response?.data?.error || 'Could not add that collaborator.');
    } finally {
      setAddingCollaborator(false);
    }
  };

  const handleRemoveCollaborator = async (email) => {
    setRemovingCollaborator(email);
    try {
      await removeCollaborator(caseId, email);
      setCaseDraft((prev) => ({ ...prev, collaborators: (prev.collaborators || []).filter((c) => c !== email) }));
    } catch {
      alert('Could not remove that collaborator. Please try again.');
    } finally {
      setRemovingCollaborator(null);
    }
  };

  const handleGenerate = async () => {
    // Regenerating throws away every existing item, including ones an admin
    // may have hand-edited — confirm since this is rare and destructive,
    // unlike the initial (empty-checklist) generation.
    if (checklist && checklist.length > 0) {
      const confirmed = window.confirm('This will replace the current checklist items with a freshly generated set. This cannot be undone. Continue?');
      if (!confirmed) return;
    }
    setGenerating(true);
    setFinalized(false);
    setGenerateError(false);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/cases/${caseId}/checklist/generate`, { config: DEFAULT_CONFIG, caseDetails: caseDraft });
      if (res.data && res.data.items) {
        setChecklist(res.data.items);
      }
    } catch {
      setGenerateError(true);
    } finally {
      setGenerating(false);
    }
  };

  const handleFinalize = async () => {
    setFinalizing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/cases/${caseId}/checklist/finalize`, {
        items: checklist,
        config: DEFAULT_CONFIG,
        finalizedAt: new Date().toISOString(),
      });
      setFinalized(true);
      fetchCases();
    } catch {
      // Finalize locally even if backend unavailable
      setFinalized(true);
    } finally {
      setFinalizing(false);
    }
  };

  const switchSection = (section) => {
    // Clicking the already-open section closes it (both can be closed at
    // once); clicking the other one opens it and implicitly closes the first,
    // since only one value is ever stored.
    setActiveSection((prev) => prev === section ? null : section);
    // "Saved." is a transient confirmation; without clearing it, it reappears
    // stale when the user comes back to Details later. Errors are kept
    // deliberately — those describe a real failed save the user still needs to see.
    setCaseDataSavedMsg('');
  };

  // Item operations — the checklist is always editable now (no separate edit
  // lock), so any mutation means whatever was last saved is now stale.
  const editItem = (id, text) => {
    setChecklist((prev) => prev.map((item) => item.id === id ? { ...item, text } : item));
    setFinalized(false);
  };

  const deleteItem = (id) => {
    setChecklist((prev) => prev.filter((item) => item.id !== id));
    setFinalized(false);
  };

  const addItem = (section, text) => {
    setChecklist((prev) => [...prev, { id: makeId(), section, text }]);
    setFinalized(false);
  };

  // Drag handlers
  const handleDragStart = (id) => { dragItem.current = id; };
  const handleDragOver = (id) => { dragOverItem.current = id; };
  const handleDrop = () => {
    if (!dragItem.current || dragItem.current === dragOverItem.current) return;
    setChecklist((prev) => {
      const items = [...prev];
      const fromIdx = items.findIndex((i) => i.id === dragItem.current);
      const toIdx = items.findIndex((i) => i.id === dragOverItem.current);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const [moved] = items.splice(fromIdx, 1);
      items.splice(toIdx, 0, moved);
      return items;
    });
    setFinalized(false);
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const itemsBySection = (key) => (checklist || []).filter((i) => i.section === key);
  const totalItems = checklist ? checklist.length : 0;

  const selectedSubCompetencies = (caseDraft?.indicatorCodes || [])
    .map(getSubCompetencyByCode)
    .filter(Boolean);
  const normalizedIndicatorSearch = indicatorSearch.trim().toLowerCase();
  const indicatorSearchResults = normalizedIndicatorSearch
    ? AACN_SUB_COMPETENCIES.filter((sc) =>
      sc.subCompetencyCode.toLowerCase().includes(normalizedIndicatorSearch) ||
      sc.subCompetency.toLowerCase().includes(normalizedIndicatorSearch)
    )
    : [];

  // Legacy cases predate ownership (ownerEmail null) — treat those as unrestricted,
  // same as before this feature existed. Only enforce owner-only once one is set.
  const isOwner = !caseDraft?.ownerEmail || caseDraft.ownerEmail === currentUserEmail;
  // Kabilah's own seeded cases (as opposed to custom UNC-created cases) are
  // locked down entirely: can't be unpublished, deleted, edited, or have their
  // checklist edited.
  const isKabilahCase = caseDraft?.source === 'standard';
  // Admins can always delete a case regardless of who owns it — ownership only
  // restricts non-admin (teacher) access to someone else's case.
  const canDelete = !isKabilahCase && (isOwner || !isTeacher);
  const isDraft = caseDraft?.status === 'draft';
  // The one workflow step collapsing this section hides is saving — surface
  // it as a dot on the header so it can't be silently skipped.
  const checklistNeedsAttention = !!caseDraft && !isKabilahCase && !checklistLoading && !finalized;
  const detailsSummary = `Room: ${caseDraft?.patient?.room || 'Not set'}`;
  const competenciesSummary = `${selectedSubCompetencies.length} sub-competenc${selectedSubCompetencies.length === 1 ? 'y' : 'ies'} selected`;
  const checklistSummary = checklistLoading
    ? 'Loading…'
    : !checklist
      ? 'No checklist yet'
      : `${totalItems} item${totalItems === 1 ? '' : 's'}`;

  const normalizedCollaboratorInput = collaboratorInput.trim().toLowerCase();
  const existingCollaboratorEmails = new Set([
    caseDraft?.ownerEmail,
    currentUserEmail,
    ...(caseDraft?.collaborators || []),
  ].filter(Boolean).map((e) => e.toLowerCase()));
  // Shown on focus even before typing (filtered further once they do), so the
  // full list of eligible people is discoverable without knowing a name first.
  const collaboratorSuggestions = collaboratorAccounts.filter((a) =>
    !existingCollaboratorEmails.has((a.email || '').toLowerCase()) &&
    (!normalizedCollaboratorInput ||
      a.email?.toLowerCase().includes(normalizedCollaboratorInput) ||
      a.name?.toLowerCase().includes(normalizedCollaboratorInput))
  ).slice(0, 6);

  return (
    <div className="root-container">
      <div className="title-container">
        <div className="title-left">
          <button className="back-button" onClick={() => { fetchCases(); setLastCasesTab(isDraft ? 'draft' : 'published'); navigate('/admin/cases'); }}>
            ← Cases
          </button>
          <div className="title-name-container" style={{ marginLeft: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <header className="name-header">{caseName}</header>
            {caseDraft?.status && (
              <span className={`case-badge ${isDraft ? 'case-badge-draft' : 'case-badge-configured'}`}>
                {isDraft ? 'Draft' : 'Published'}
              </span>
            )}
            {isKabilahCase && (
              <span className="account-count-indicator" title="Kabilah's own cases can't be unpublished, deleted, or edited.">
                Kabilah Case
              </span>
            )}
          </div>
        </div>
        <div className="reload-button-container" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {isDraft ? (
            <button className="title-button" onClick={handlePublish} disabled={publishing}>
              {publishing ? 'Publishing...' : 'Publish'}
            </button>
          ) : !isKabilahCase && (
            <button className="edit-checklist-btn" onClick={handleUnpublish} disabled={unpublishing}>
              {unpublishing ? 'Unpublishing...' : 'Unpublish'}
            </button>
          )}
          {canDelete && (
            confirmDelete ? (
              <div className="delete-confirm">
                <span className="delete-confirm-text">Delete this case?</span>
                <button className="delete-confirm-yes" onClick={handleDelete} disabled={deleting}>
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
                <button className="delete-confirm-no" onClick={() => setConfirmDelete(false)} disabled={deleting}>
                  Cancel
                </button>
              </div>
            ) : (
              <button className="delete-btn" onClick={() => setConfirmDelete(true)}>
                Delete Case
              </button>
            )
          )}
        </div>
      </div>

      <div className="component-container">
        <div className="checklist-config-content">

          {/* Case data */}
          <div className="case-manage-section">
            <button
              type="button"
              className="case-manage-collapsible-header"
              aria-expanded={activeSection === 'details'}
              onClick={() => switchSection('details')}
            >
              <span className="case-manage-collapsible-title">Case Details</span>
              <span className="case-manage-collapsible-summary">{detailsSummary}</span>
              <span className="case-manage-collapsible-chevron">{activeSection === 'details' ? '▾' : '▸'}</span>
            </button>
            <div className="case-manage-collapsible-body" hidden={activeSection !== 'details'}>
            {!caseDraft ? (
              <p className="config-intro">Loading case details...</p>
            ) : (
              <>
                <div className="review-section">
                  <label className="review-field-label">Case Name <span className="review-required">*</span></label>
                  <input
                    className={`review-name-input ${isKabilahCase ? 'review-field-readonly' : ''}`}
                    value={caseDraft.caseName || ''}
                    onChange={(e) => { if (!isKabilahCase) setDraftField('caseName', e.target.value); }}
                    placeholder="Enter case name"
                    readOnly={isKabilahCase}
                  />
                </div>

                {caseDraft.ownerEmail && !isKabilahCase && (
                  <div className="review-section">
                    <label className="review-field-label">Owner &amp; Collaborators</label>
                    <div className="case-owner-row">
                      <span className="class-tag-chip">Owner: {caseDraft.ownerEmail}</span>
                      {(caseDraft.collaborators || []).map((email) => (
                        <span key={email} className="case-collaborator-chip">
                          {email}
                          {isOwner && (
                            <button
                              className="case-collaborator-remove"
                              onClick={() => handleRemoveCollaborator(email)}
                              disabled={removingCollaborator === email}
                              title="Remove collaborator"
                            >×</button>
                          )}
                        </span>
                      ))}
                    </div>
                    {isOwner && (
                      <div className="case-collaborator-add-wrap">
                        <div className="case-collaborator-add-row">
                          <input
                            type="email"
                            className="review-name-input"
                            placeholder="Add collaborator by name or email..."
                            value={collaboratorInput}
                            onChange={(e) => { setCollaboratorInput(e.target.value); setCollaboratorError(''); }}
                            onFocus={() => setShowCollaboratorSuggestions(true)}
                            // Delay so a click on a suggestion (below) registers
                            // before the list disappears — blur fires first.
                            onBlur={() => setTimeout(() => setShowCollaboratorSuggestions(false), 150)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleAddCollaborator(); }}
                            disabled={addingCollaborator}
                          />
                          <button
                            className="title-button"
                            onClick={() => handleAddCollaborator()}
                            disabled={addingCollaborator || !collaboratorInput.trim()}
                          >
                            {addingCollaborator ? 'Adding...' : 'Add'}
                          </button>
                        </div>
                        {showCollaboratorSuggestions && collaboratorSuggestions.length > 0 && (
                          <div className="case-collaborator-suggestions">
                            {collaboratorSuggestions.map((account) => (
                              <button
                                key={account.email}
                                type="button"
                                className="case-collaborator-suggestion-row"
                                onClick={() => handleAddCollaborator(account.email)}
                                disabled={addingCollaborator}
                              >
                                <span className="case-collaborator-suggestion-name">{account.name || account.email}</span>
                                <span className="case-collaborator-suggestion-email">{account.email}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {collaboratorError && <span className="review-field-error">{collaboratorError}</span>}
                  </div>
                )}

                <div className="review-section">
                  <label className="review-field-label">MRN <span className="review-field-note">set at creation — cannot be changed</span></label>
                  <input
                    className={`review-name-input review-field-readonly ${caseDataFieldErrors.mrn ? 'review-field-input-error' : ''}`}
                    value={caseDraft.mrn || ''}
                    readOnly
                    placeholder="Not set"
                  />
                  {caseDataFieldErrors.mrn && <span className="review-field-error">{caseDataFieldErrors.mrn}</span>}
                </div>

                <div className="review-section">
                  <label className="review-field-label">Patient Information</label>
                  <div className="review-fields-grid">
                    {PATIENT_FIELDS.map(({ key, label, wide, required, type }) => {
                      const locked = key === 'dateOfBirth' || key === 'room' || isKabilahCase;
                      return (
                        <div key={key} className={`review-editable-field ${wide ? 'review-field-wide' : ''}`}>
                          <label className="review-field-key">
                            {label}
                            {key === 'room' ? (
                              <span className="review-field-note">set at creation — cannot be changed</span>
                            ) : key === 'dateOfBirth' ? (
                              <span className="review-field-note">auto-calculated from age</span>
                            ) : (
                              required && <span className="review-required">*</span>
                            )}
                          </label>
                          <input
                            className={`review-field-input ${locked ? 'review-field-readonly' : ''} ${caseDataFieldErrors[key] ? 'review-field-input-error' : ''}`}
                            type={type || 'text'}
                            value={caseDraft.patient?.[key] || ''}
                            onChange={(e) => {
                              if (locked) return;
                              if (key === 'age') handleAgeChange(e.target.value);
                              else setPatientField(key, e.target.value);
                            }}
                            readOnly={locked}
                            placeholder={key === 'room' ? 'Not set' : ''}
                          />
                          {caseDataFieldErrors[key] && <span className="review-field-error">{caseDataFieldErrors[key]}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {caseDraft.reportItems?.length > 0 && (
                  <div className="review-section">
                    <label className="review-field-label">Clinical Report</label>
                    <div className="report-items-list">
                      {caseDraft.reportItems.map((item, i) => (
                        <div key={item.name} className="report-item-field">
                          <label className="report-item-label">{item.name}</label>
                          <textarea
                            className={`report-item-textarea ${isKabilahCase ? 'review-field-readonly' : ''}`}
                            value={item.contents || ''}
                            onChange={(e) => { if (!isKabilahCase) setReportItemContents(i, e.target.value); }}
                            readOnly={isKabilahCase}
                            rows={2}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!isKabilahCase && caseDataErrorMsg && <div className="upload-error-banner">{caseDataErrorMsg}</div>}

                {!isKabilahCase && (
                  <div className="upload-actions">
                    <button
                      className="generate-button"
                      onClick={handleSaveCaseData}
                      disabled={!caseDraft.caseName?.trim() || !caseDraft.patient?.dateOfBirth?.trim() || savingCaseData}
                    >
                      {savingCaseData ? 'Saving...' : 'Save Case Details'}
                    </button>
                    {caseDataSavedMsg && <span className="case-manage-saved-note">{caseDataSavedMsg}</span>}
                  </div>
                )}
              </>
            )}
            </div>
          </div>

          {/* Checklist config */}
          <div className="case-manage-section">
            <button
              type="button"
              className="case-manage-collapsible-header"
              aria-expanded={activeSection === 'checklist'}
              onClick={() => switchSection('checklist')}
            >
              <span className="case-manage-collapsible-title">Handoff Checklist</span>
              <span className="case-manage-collapsible-summary">{checklistSummary}</span>
              {checklistNeedsAttention && (
                <span
                  className="case-manage-attention-dot"
                  role="img"
                  aria-label="Checklist not saved yet"
                  title="Checklist not saved yet"
                />
              )}
              <span className="case-manage-collapsible-chevron">{activeSection === 'checklist' ? '▾' : '▸'}</span>
            </button>
            <div className="case-manage-collapsible-body" hidden={activeSection !== 'checklist'}>
            {checklistLoading && <p className="config-intro">Loading checklist...</p>}

            {/* Checklist editor — always editable (no separate edit-lock step);
                Save Checklist below persists whatever's currently here. */}
            {checklist && !generating && (
              <div className="checklist-editor">
                <div className="checklist-editor-header">
                  <span className="checklist-editor-title">Checklist</span>
                  {!isKabilahCase && (
                    <button className="edit-checklist-btn" onClick={handleGenerate} disabled={generating}>
                      {generating ? 'Generating...' : 'Regenerate Checklist'}
                    </button>
                  )}
                </div>

                {SECTIONS.map((section) => {
                  const items = itemsBySection(section.key);
                  return (
                    <ChecklistSection
                      key={section.key}
                      sectionKey={section.key}
                      title={section.title}
                      hint={section.hint}
                      items={items}
                      onEdit={editItem}
                      onDelete={deleteItem}
                      onAdd={addItem}
                      onDragStart={handleDragStart}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      finalized={isKabilahCase}
                    />
                  );
                })}
              </div>
            )}

            {!isKabilahCase && !checklistLoading && (
              <div className="upload-actions">
                {checklist ? (
                  <button
                    className="generate-button"
                    onClick={handleFinalize}
                    disabled={finalizing || totalItems === 0}
                  >
                    {finalizing ? 'Saving...' : 'Save Checklist'}
                  </button>
                ) : (
                  <button
                    className="generate-button"
                    onClick={handleGenerate}
                    disabled={generating}
                  >
                    {generating ? 'Generating...' : 'Generate Checklist'}
                  </button>
                )}
                {checklist && !generating && finalized && (
                  <span className="case-manage-saved-note">Saved.</span>
                )}
                {generateError && (
                  <span className="generate-error">Could not generate checklist. Please try again.</span>
                )}
              </div>
            )}
            </div>
          </div>

          {/* AACN sub-competencies */}
          <div className="case-manage-section">
            <button
              type="button"
              className="case-manage-collapsible-header"
              aria-expanded={activeSection === 'competencies'}
              onClick={() => switchSection('competencies')}
            >
              <span className="case-manage-collapsible-title">AACN Sub-Competencies</span>
              <span className="case-manage-collapsible-summary">{competenciesSummary}</span>
              <span className="case-manage-collapsible-chevron">{activeSection === 'competencies' ? '▾' : '▸'}</span>
            </button>
            <div className="case-manage-collapsible-body" hidden={activeSection !== 'competencies'}>
            {!caseDraft ? (
              <p className="config-intro">Loading case details...</p>
            ) : (
              <div className="review-section">
                <div className="indicator-picker-box">
                  <div className="indicator-picker-box-header">
                    <label className="review-field-label" style={{ marginBottom: 0 }}>
                      What this case is graded on
                    </label>
                    <span className="indicator-picker-total-badge">
                      {aacnLoading ? 'Loading…' : `${selectedSubCompetencies.length} of ${AACN_SUB_COMPETENCIES.length} selected`}
                    </span>
                  </div>

                  {!isKabilahCase && (
                    <div className="indicator-picker-search-wrap">
                      <span className="indicator-picker-search-icon">⌕</span>
                      <input
                        type="text"
                        className="indicator-picker-search-input"
                        placeholder={aacnLoading ? 'Loading AACN sub-competencies…' : `Search all ${AACN_SUB_COMPETENCIES.length} AACN sub-competencies to add...`}
                        value={indicatorSearch}
                        onChange={(e) => setIndicatorSearch(e.target.value)}
                        disabled={aacnLoading}
                      />
                    </div>
                  )}

                  {!isKabilahCase && normalizedIndicatorSearch && (
                    <div className="indicator-domain-list indicator-picker-search-results">
                      {indicatorSearchResults.length === 0 ? (
                        <p className="chat-card-hint">No matching sub-competencies.</p>
                      ) : (
                        indicatorSearchResults.map((sc) => {
                          const isSelected = (caseDraft.indicatorCodes || []).includes(sc.subCompetencyCode);
                          return (
                            <div
                              key={sc.subCompetencyCode}
                              className={`indicator-row ${isSelected ? 'indicator-row-selected' : ''}`}
                              onClick={() => toggleIndicatorCode(sc.subCompetencyCode)}
                            >
                              <span className="indicator-row-text">
                                <span className="indicator-row-code">{sc.subCompetencyCode}</span> {sc.subCompetency}
                              </span>
                              <span className="indicator-picker-row-action">{isSelected ? '✓ added — click to remove' : '+ add'}</span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}

                  <div className="indicator-picker-selected-wrap">
                    <span className="indicator-picker-col-label">Selected ({selectedSubCompetencies.length})</span>
                    {selectedSubCompetencies.length > 0 ? (
                      <div className="indicator-chip-row">
                        {selectedSubCompetencies.map((sc) => (
                          isKabilahCase ? (
                            <span key={sc.subCompetencyCode} className="indicator-chip-tag">
                              <span className="indicator-chip-tag-text">
                                <span className="indicator-chip-tag-code">{sc.subCompetencyCode}</span> {sc.subCompetency}
                              </span>
                            </span>
                          ) : (
                            <button
                              key={sc.subCompetencyCode}
                              type="button"
                              className="indicator-chip-tag"
                              onClick={() => toggleIndicatorCode(sc.subCompetencyCode)}
                              title="Click to remove"
                            >
                              <span className="indicator-chip-tag-text">
                                <span className="indicator-chip-tag-code">{sc.subCompetencyCode}</span> {sc.subCompetency}
                              </span>
                              <span className="indicator-chip-tag-remove">×</span>
                            </button>
                          )
                        ))}
                      </div>
                    ) : (
                      <p className="chat-card-hint">No sub-competencies selected yet — search above to add some.</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            </div>
          </div>

        </div>
      </div>

      <Footer />
    </div>
  );
}

export default CaseManage;
